import { useMemo } from 'react';
import { Else, If, Then, When } from 'react-if';
import cns from 'classnames';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { CheckIcon } from '@zep/icons';
import { SingularSubscriptionPaymentResponse } from '../../../../../types';
import { fCurrency } from '../../../../../utils';
import { usePremiumOptionName } from '../../subscriptionUtil';
import S from './ReusablePaymentInformation.module.scss';
export const ReusablePaymentInformation = (props: PaymentInformationProps) => {
  const {
    paymentInformation
  } = props;
  const optionKeys = useMemo(() => Object.keys(paymentInformation.spacePlan.options).filter(
  // @ts-ignore
  key => paymentInformation.spacePlan.options[key]), [paymentInformation.spacePlan.options]);
  return <>
      <ProductName {...props} data-sentry-element="ProductName" data-sentry-source-file="ReusablePaymentInformation.tsx" />
      <ProductPrice {...props} data-sentry-element="ProductPrice" data-sentry-source-file="ReusablePaymentInformation.tsx" />
      <ProductDetails {...props} data-sentry-element="ProductDetails" data-sentry-source-file="ReusablePaymentInformation.tsx" />
      <When condition={optionKeys.length} data-sentry-element="When" data-sentry-source-file="ReusablePaymentInformation.tsx">
        <PremiumOptions {...props} optionKeys={optionKeys} data-sentry-element="PremiumOptions" data-sentry-source-file="ReusablePaymentInformation.tsx" />
      </When>
    </>;
};
const ProductName = ({
  productName
}: PaymentInformationProps) => {
  const {
    t
  } = useTranslation(['subscription', 'business'], {
    useSuspense: true
  });
  return <div className={cns(S.information_row, S.no_margin_top)} data-sentry-component="ProductName" data-sentry-source-file="ReusablePaymentInformation.tsx">
      <div className={S.row_title}>
        {t('business:business_payments_product_name')}
      </div>
      <div>{productName}</div>
    </div>;
};
const ProductPrice = ({
  paymentInformation
}: PaymentInformationProps) => {
  const {
    t
  } = useTranslation(['subscription', 'business'], {
    useSuspense: true
  });
  const {
    currency
  } = paymentInformation.spacePlan;
  const paymentVatAmount = paymentInformation.payment?.vatAmount;
  const vatAmount = typeof paymentVatAmount === 'number' ? paymentVatAmount : paymentInformation.priceInfo?.vatAmount ?? 0;
  const priceBeforeVat = typeof paymentVatAmount === 'number' ? (paymentInformation.payment?.totalPrice ?? 0) - paymentVatAmount : paymentInformation.priceInfo?.priceBeforeVat ?? paymentInformation.spacePlan.salePrice;
  const currencyString = currency === 'KRW' ? '₩ ' : '$ ';
  return <>
      <div className={S.information_row}>
        {/* 가격 row는 공급가(VAT 전). vatAmount > 0(신정책 과세 건)이면 'VAT 포함'
            라벨이 금액 오해를 만들므로 'VAT 제외' 라벨을 쓴다 (Gate E).
            vatAmount=0(legacy/USD)은 기존 표기 유지. */}
        <div className={S.row_title}>
          {vatAmount > 0 || currency !== 'KRW' ? t('business:businessPage.price.seperateVAT') : t('business:business_payments_product_price')}
        </div>
        <div>
          {fCurrency(priceBeforeVat, currency, {
          position: 'pre',
          currencyString
        })}
        </div>
      </div>
      {vatAmount > 0 && <div className={S.information_row}>
          <div className={S.row_title}>
            {t('business:business_payments_vat')}
          </div>
          <div>
            {fCurrency(vatAmount, currency, {
          position: 'pre',
          currencyString
        })}
          </div>
        </div>}
    </>;
};
const ProductDetails = ({
  paymentInformation
}: PaymentInformationProps) => {
  const {
    t
  } = useTranslation(['subscription', 'business'], {
    useSuspense: true
  });
  return <div className={S.information_row} data-sentry-component="ProductDetails" data-sentry-source-file="ReusablePaymentInformation.tsx">
      <div className={S.row_title}>
        {t('business:business_payments_product_content')}
      </div>
      <div className={S.premium_content_wrapper}>
        <div className={S.premium_content_item}>
          <div className={S.premium_content_item_title}>
            {t('subscription:onetimeInvoice.productTitle.plan')}
          </div>
          <div className={S.item_content}>
            {paymentInformation.spacePlan.planType.capitalize()}
          </div>
        </div>
        <div className={S.premium_content_item}>
          <div className={S.premium_content_item_title}>
            {t('subscription:onetimeInvoice.productTitle.ccu')}
          </div>
          <div className={S.item_content}>
            <If condition={paymentInformation.spacePlan.maxCcu < 0} data-sentry-element="If" data-sentry-source-file="ReusablePaymentInformation.tsx">
              <Then data-sentry-element="Then" data-sentry-source-file="ReusablePaymentInformation.tsx">{t('subscription:pricing.calculation.barUnlimited')}</Then>
              <Else data-sentry-element="Else" data-sentry-source-file="ReusablePaymentInformation.tsx">
                {t('subscription:pricing.plansCommon.peopleNumber', {
                ccu: paymentInformation.spacePlan.maxCcu
              })}
              </Else>
            </If>
          </div>
        </div>
        <div className={S.premium_content_item}>
          <div className={S.premium_content_item_title}>
            {t('subscription:onetimeInvoice.productTitle.startDate')}
          </div>
          <div className={S.item_content}>
            {format(new Date(paymentInformation.spacePlan.startDate), 'yyyy.MM.dd')}
          </div>
        </div>
        <div className={S.premium_content_item}>
          <div className={S.premium_content_item_title}>
            {t('subscription:onetimeInvoice.productTitle.endDate')}
          </div>
          <div className={S.item_content}>
            {format(new Date(paymentInformation.spacePlan.endDate), 'yyyy.MM.dd')}
          </div>
        </div>
        <div className={S.premium_content_item}>
          <div className={S.premium_content_item_title}>
            {t('subscription:onetimeInvoice.productTitle.spaceLink')}
          </div>
          <div className={S.item_content}>
            <a href={`https://${window.location.host}/spaces/${paymentInformation.spacePlan.spaceHashId}`} target={'_blank'}>
              https://{window.location.host}/spaces/
              {paymentInformation.spacePlan.spaceHashId}
            </a>
          </div>
        </div>
        <div className={cns(S.notice_message, S.item_content)}>
          {paymentInformation.paymentRequest.noticeMessage}
        </div>
      </div>
    </div>;
};
const PremiumOptions = ({
  paymentInformation,
  optionKeys
}: PaymentInformationProps & {
  optionKeys: string[];
}) => {
  const {
    t
  } = useTranslation(['subscription', 'business'], {
    useSuspense: true
  });
  const optionNames = usePremiumOptionName();
  return <div className={cns(S.information_row)} data-sentry-component="PremiumOptions" data-sentry-source-file="ReusablePaymentInformation.tsx">
      <div className={S.row_title}>
        {t('subscription:onetimeInvoice.productTitle.planInformation', {
        planName: paymentInformation.spacePlan.planType.capitalize()
      })}
      </div>
      <div>
        <ul>
          {optionKeys.map(key => <When key={key} condition={
        // @ts-ignore
        optionNames[key]}>
              <li className={S.options}>
                <CheckIcon width={16} height={16} style={{
              display: 'inline'
            }} />
                {
            // @ts-ignore
            optionNames[key]}
              </li>
            </When>)}
        </ul>
      </div>
    </div>;
};
export interface PaymentInformationProps {
  customerName: string;
  paymentInformation: SingularSubscriptionPaymentResponse;
  productName: string;
}